/**
 * Simulation Redux Selectors
 */

import { createSelector } from '@reduxjs/toolkit'
import type { SimulationState } from './simulation.slice'
import { computeStepStatuses } from '../domain/utils/simulation.utils'

const selectSimulationState = (state: any): SimulationState => {
  if (state.simulation) return state.simulation
  if (state.shipments?.simulation) return state.shipments.simulation
  return state
}

export const selectSimulationOrderNumber = createSelector(
  selectSimulationState,
  (state) => state.orderNumber
)

export const selectSimulationLifecycle = createSelector(
  selectSimulationState,
  (state) => state.lifecycle
)

export const selectSimulationLoadingLifecycle = createSelector(
  selectSimulationState,
  (state) => state.loadingLifecycle
)

export const selectSimulationLifecycleError = createSelector(
  selectSimulationState,
  (state) => state.lifecycleError
)

export const selectSimulationExecutingStep = createSelector(
  selectSimulationState,
  (state) => state.executingStep
)

export const selectSimulationStepError = createSelector(
  selectSimulationState,
  (state) => state.stepError
)

export const selectSimulationStepErrorDetails = createSelector(
  selectSimulationState,
  (state) => state.stepErrorDetails
)

export const selectSimulationActivityLog = createSelector(
  selectSimulationState,
  (state) => state.activityLog
)

export const selectSimulationSteps = createSelector(
  selectSimulationLifecycle,
  (lifecycle) => {
    if (!lifecycle) return null
    return computeStepStatuses(lifecycle)
  }
)

export const selectActiveStep = createSelector(
  selectSimulationSteps,
  (steps) => {
    if (!steps) return null
    return steps.find((s) => s.status === 'active') ?? null
  }
)

export const selectSimulationLoading = createSelector(
  selectSimulationLoadingLifecycle,
  selectSimulationExecutingStep,
  (loadingLifecycle, executingStep) => loadingLifecycle || executingStep !== null
)

// =============================================
// Factory / Zone / Device selectors
// =============================================

export const selectSimulationSelectedFactory = createSelector(
  selectSimulationState,
  (state) => state.selectedFactory
)

export const selectSimulationLoadingFactoryContext = createSelector(
  selectSimulationState,
  (state) => state.loadingFactoryContext
)

export const selectSimulationFactoryContextError = createSelector(
  selectSimulationState,
  (state) => state.factoryContextError
)

export const selectSimulationEntryZone = createSelector(
  selectSimulationState,
  (state) => state.entryZone
)

export const selectSimulationWeighbridgeZone = createSelector(
  selectSimulationState,
  (state) => state.weighbridgeZone
)

export const selectSimulationExitZone = createSelector(
  selectSimulationState,
  (state) => state.exitZone
)

export const selectSimulationEntryReaders = createSelector(
  selectSimulationState,
  (state) => state.entryReaders
)

export const selectSimulationEntryCameras = createSelector(
  selectSimulationState,
  (state) => state.entryCameras
)

export const selectSimulationWeighbridgeReaders = createSelector(
  selectSimulationState,
  (state) => state.weighbridgeReaders
)

export const selectSimulationWeighbridgeCameras = createSelector(
  selectSimulationState,
  (state) => state.weighbridgeCameras
)

export const selectSimulationExitReaders = createSelector(
  selectSimulationState,
  (state) => state.exitReaders
)

export const selectSimulationExitCameras = createSelector(
  selectSimulationState,
  (state) => state.exitCameras
)

export const selectSimulationScanMode = createSelector(
  selectSimulationState,
  (state) => state.scanMode
)

export const selectSimulationLastScanResponse = createSelector(
  selectSimulationState,
  (state) => state.lastScanResponse
)

export const selectSimulationLastScanStep = createSelector(
  selectSimulationState,
  (state) => state.lastScanStep
)
